import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MessagingProvider } from './contexts/MessagingContext';

// Pages
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import ProductsPage from './pages/ProductsPage';
import NotFoundPage from './pages/NotFoundPage';
import AboutPage from './pages/AboutPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ContactPage from './pages/ContactPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import MessagingPage from './pages/MessagingPage';

// Auth Pages
import SignInPage from './pages/auth/LoginPage';
import SignUpPage from './pages/auth/SignupPage';
import ForgotPasswordPage from './pages/auth/ResetPasswordPage';

// Resource Pages
import ResourcesPage from './pages/resources/ResourcesPage';
import ResourceDetailPage from './pages/resources/ResourceDetailPage';
import ResourceFormPage from './pages/resources/ResourceFormPage';
import ResourceManagePage from './pages/resources/ResourceManagePage';
import ResourceCategoryPage from './pages/resources/ResourceCategoryPage';
import ResourceVoicePage from './pages/resources/ResourceVoicePage';

// Product Pages
import ProductCreatePage from './pages/products/ProductCreatePage';
import ProductDetailPage from './pages/products/ProductDetailPage';

// Analysis Pages
import ProductInputPage from './pages/analysis/ProductInputPage';
import ProductAnalysisPage from './pages/analysis/ProductAnalysisPage';
import FinancialInputPage from './pages/analysis/FinancialInputPage';
import FinancialAnalysisPage from './pages/analysis/FinancialAnalysisPage';
import TeamAssetsInputPage from './pages/analysis/TeamAssetsInputPage';

// Pitch Deck Pages
import PitchDeckViewPage from './pages/pitchdeck/PitchDeckViewPage';
import UploadPitchDeckPage from './pages/pitchdeck/UploadPitchDeckPage';

// Gallery Pages
import GalleryPage from './pages/gallery/GalleryPage';
import GalleryViewPage from './pages/gallery/GalleryViewPage';

// Stage-specific Pages
import StageInputPage from './pages/products/StageInputPage';
import StageDetailsPage from './pages/products/StageDetailsPage';
import CategoryInputPage from './pages/products/CategoryInputPage';
import ClassInputPage from './pages/products/ClassInputPage';
import ClassDetailsPage from './pages/products/ClassDetailsPage';

// Payment Pages
import SubscriptionPage from './pages/payment/SubscriptionPage';
import PaymentSuccessPage from './pages/payment/PaymentSuccessPage';
import PaymentCancelPage from './pages/payment/PaymentCancelPage';

// Management Pages
import ManagementPage from './pages/management/ManagementPage';
import TechnologyPage from './pages/management/TechnologyPage';
import AssetsPage from './pages/management/AssetsPage';
import PatentsPage from './pages/management/PatentsPage';
import TeamMembersPage from './pages/management/TeamMembersPage';
import FundingPage from './pages/management/FundingPage';
import SpendingPage from './pages/management/SpendingPage';

// Profile Pages
import InvestorProfileCreatePage from './pages/profiles/InvestorProfileCreatePage';
import ServiceProfileCreatePage from './pages/profiles/ServiceProfileCreatePage';
import PharmaceuticalProfileCreatePage from './pages/profiles/PharmaceuticalProfileCreatePage';
import InvestorProfileEditPage from './pages/profiles/InvestorProfileEditPage';
import ServiceProfileEditPage from './pages/profiles/ServiceProfileEditPage';
import PharmaceuticalProfileEditPage from './pages/profiles/PharmaceuticalProfileEditPage';
import StartupProfileEditPage from './pages/profiles/StartupProfileEditPage';
import ParticipantProfilesPage from './pages/profiles/ParticipantProfilesPage';
import ProfileDetailPage from './pages/profiles/ProfileDetailPage';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  // Show loading screen while authentication state is being determined
  if (loading) return <LoadingScreen message="Authenticating..." />;
  
  // Redirect to sign in if not authenticated
  if (!user) return <Navigate to="/signin" replace />;
  
  return children;
};

// Public route component (only accessible when not logged in)
const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  // Show loading screen while authentication state is being determined
  if (loading) return <LoadingScreen message="Authenticating..." />;
  
  // Redirect to dashboard if already authenticated
  if (user) return <Navigate to="/dashboard" replace />;
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <MessagingProvider>
        <Router>
          <div className="flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-grow">
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                <Route path="/contact" element={<ContactPage />} />
                
                {/* Public only routes (redirect if logged in) */}
                <Route path="/signin" element={
                  <PublicOnlyRoute>
                    <SignInPage />
                  </PublicOnlyRoute>
                } />
                <Route path="/signup" element={
                  <PublicOnlyRoute>
                    <SignUpPage />
                  </PublicOnlyRoute>
                } />
                <Route path="/forgot-password" element={
                  <PublicOnlyRoute>
                    <ForgotPasswordPage />
                  </PublicOnlyRoute>
                } />
                
                {/* Gallery routes */}
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/gallery/pitch/:id" element={<GalleryViewPage />} />
                
                {/* Protected routes (require authentication) */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } />
                <Route path="/messaging" element={
                  <ProtectedRoute>
                    <MessagingPage />
                  </ProtectedRoute>
                } />
                <Route path="/profile/startup/edit" element={
                  <ProtectedRoute>
                    <StartupProfileEditPage />
                  </ProtectedRoute>
                } />
                <Route path="/profile/investor/create" element={
                  <ProtectedRoute>
                    <InvestorProfileCreatePage />
                  </ProtectedRoute>
                } />
                <Route path="/profile/investor/edit" element={
                  <ProtectedRoute>
                    <InvestorProfileEditPage />
                  </ProtectedRoute>
                } />
                <Route path="/profile/service/create" element={
                  <ProtectedRoute>
                    <ServiceProfileCreatePage />
                  </ProtectedRoute>
                } />
                <Route path="/profile/service/edit" element={
                  <ProtectedRoute>
                    <ServiceProfileEditPage />
                  </ProtectedRoute>
                } />
                <Route path="/profile/pharmaceutical/create" element={
                  <ProtectedRoute>
                    <PharmaceuticalProfileCreatePage />
                  </ProtectedRoute>
                } />
                <Route path="/profile/pharmaceutical/edit" element={
                  <ProtectedRoute>
                    <PharmaceuticalProfileEditPage />
                  </ProtectedRoute>
                } />
                <Route path="/products" element={
                  <ProtectedRoute>
                    <ProductsPage />
                  </ProtectedRoute>
                } />
                
                {/* Product routes */}
                <Route path="/products/create" element={
                  <ProtectedRoute>
                    <ProductCreatePage />
                  </ProtectedRoute>
                } />
                <Route path="/products/:id" element={
                  <ProtectedRoute>
                    <ProductDetailPage />
                  </ProtectedRoute>
                } />
                
                {/* Stage-specific product input routes */}
                <Route path="/products/:stage/input" element={
                  <ProtectedRoute>
                    <StageInputPage />
                  </ProtectedRoute>
                } />
                
                {/* Stage-specific details input routes */}
                <Route path="/products/:id/details/:stage" element={
                  <ProtectedRoute>
                    <StageDetailsPage />
                  </ProtectedRoute>
                } />
                
                {/* Device class-specific routes */}
                <Route path="/products/device/:deviceClass/input" element={
                  <ProtectedRoute>
                    <ClassInputPage />
                  </ProtectedRoute>
                } />
                
                {/* Device class-specific details routes */}
                <Route path="/products/:id/details/device/:deviceClass" element={
                  <ProtectedRoute>
                    <ClassDetailsPage />
                  </ProtectedRoute>
                } />
                
                {/* Category-specific product input routes */}
                <Route path="/products/category/:category/input" element={
                  <ProtectedRoute>
                    <CategoryInputPage />
                  </ProtectedRoute>
                } />
                
                {/* Analysis routes */}
                <Route path="/products/:id/input" element={
                  <ProtectedRoute>
                    <ProductInputPage />
                  </ProtectedRoute>
                } />
                <Route path="/products/:id/analysis" element={
                  <ProtectedRoute>
                    <ProductAnalysisPage />
                  </ProtectedRoute>
                } />
                <Route path="/products/:id/financial" element={
                  <ProtectedRoute>
                    <FinancialInputPage />
                  </ProtectedRoute>
                } />
                <Route path="/products/:id/financialanalysis" element={
                  <ProtectedRoute>
                    <FinancialAnalysisPage />
                  </ProtectedRoute>
                } />
                <Route path="/products/:id/team-assets" element={
                  <ProtectedRoute>
                    <TeamAssetsInputPage />
                  </ProtectedRoute>
                } />
                
                {/* Pitch Deck routes */}
                <Route path="/products/:id/pitchdeck" element={
                  <ProtectedRoute>
                    <Navigate to={window.location.pathname.replace('/pitchdeck', '/uploadpitchdeck')} replace />
                  </ProtectedRoute>
                } />
                <Route path="/products/:id/pitchdeckview" element={
                  <ProtectedRoute>
                    <PitchDeckViewPage />
                  </ProtectedRoute>
                } />
                <Route path="/products/:id/uploadpitchdeck" element={
                  <ProtectedRoute>
                    <UploadPitchDeckPage />
                  </ProtectedRoute>
                } />
                
                {/* Payment routes */}
                <Route path="/subscription" element={
                  <ProtectedRoute>
                    <SubscriptionPage />
                  </ProtectedRoute>
                } />
                <Route path="/payment/success" element={
                  <ProtectedRoute>
                    <PaymentSuccessPage />
                  </ProtectedRoute>
                } />
                <Route path="/payment/cancel" element={
                  <ProtectedRoute>
                    <PaymentCancelPage />
                  </ProtectedRoute>
                } />

                {/* Management routes */}
                <Route path="/management" element={
                  <ProtectedRoute>
                    <ManagementPage />
                  </ProtectedRoute>
                } />
                <Route path="/management/technologies" element={
                  <ProtectedRoute>
                    <TechnologyPage />
                  </ProtectedRoute>
                } />
                <Route path="/management/assets" element={
                  <ProtectedRoute>
                    <AssetsPage />
                  </ProtectedRoute>
                } />
                <Route path="/management/patents" element={
                  <ProtectedRoute>
                    <PatentsPage />
                  </ProtectedRoute>
                } />
                <Route path="/management/team" element={
                  <ProtectedRoute>
                    <TeamMembersPage />
                  </ProtectedRoute>
                } />
                <Route path="/management/funding" element={
                  <ProtectedRoute>
                    <FundingPage />
                  </ProtectedRoute>
                } />
                <Route path="/management/spending" element={
                  <ProtectedRoute>
                    <SpendingPage />
                  </ProtectedRoute>
                } />
                
                {/* Resource routes */}
                <Route path="/resources" element={<ResourcesPage />} />
                <Route path="/resources/manage" element={
                  <ProtectedRoute>
                    <ResourceManagePage />
                  </ProtectedRoute>
                } />
                <Route path="/resources/categories" element={
                  <ProtectedRoute>
                    <ResourceCategoryPage />
                  </ProtectedRoute>
                } />
                <Route path="/resources/new" element={
                  <ProtectedRoute>
                    <ResourceFormPage />
                  </ProtectedRoute>
                } />
                <Route path="/resources/edit/:id" element={
                  <ProtectedRoute>
                    <ResourceFormPage />
                  </ProtectedRoute>
                } />
                <Route path="/resources/voice/:id" element={
                  <ProtectedRoute>
                    <ResourceVoicePage />
                  </ProtectedRoute>
                } />
                <Route path="/resources/:slug" element={<ResourceDetailPage />} />
                
                {/* Profile routes */}
                <Route path="/profiles" element={<ParticipantProfilesPage />} />
                <Route path="/profiles/:type/:userId" element={<ProfileDetailPage />} />
                
                {/* 404 route */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </Router>
      </MessagingProvider>
    </AuthProvider>
  );
}

export default App; 